import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import withSizes from 'react-sizes';
import { useDispatch } from 'react-redux';
import VisibilitySensor from 'react-visibility-sensor';
import { useScrollData } from 'scroll-data-hook';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import {
  getSavedAssessments,
  getTakenAssessments,
} from 'containers/Assessments/actions';
import Carousel from 'components/ResourcesCarousel';
import ResourceItemSelector from 'components/ResourceItemSelector';
import { getFormattedType } from 'components/ResourceItemSelector/utils';
import Mixpanel from 'utils/mixpanelService';
import { filterSelfReferencingResources } from 'containers/TopicCenter/utils';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: ({ isResourcePage }) => (isResourcePage ? 60 : 20),
    [theme.breakpoints.down('sm')]: {
      padding: '0 18px !important',
    },
    [theme.breakpoints.down('480')]: {
      position: 'relative',
    },
  },
  viewMore: {
    [theme.breakpoints.down('480')]: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '100%',
      textAlign: 'center',
      margin: 0,
      padding: '150px 0 40px 0 !important',
      backgroundImage:
        'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,0.74), rgba(255,255,255,1))',
    },
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  carouselContainer: {
    position: 'relative',
    [theme.breakpoints.up('sm')]: {
      paddingLeft: '0 !important',
      paddingRight: '0 !important',
    },
  },
  viewAllContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  viewAllButton: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  viewAllText: {
    alignItems: 'center',
    ...theme.typography.overline,
  },
  viewAllIcon: {
    marginLeft: 10,
    marginBottom: 2,
  },
  itemsWrapper: {
    '& > div': {
      maxWidth: '100%',
    },
  },
}));

const ItemPageResourcesRow = ({
  col,
  data,
  header,
  isMobile,
  isTablet,
  pageData,
  isResourcePage = true,
}) => {
  const classes = useStyles({ isResourcePage });
  const dispatch = useDispatch();

  const { scrolling } = useScrollData();

  useEffect(() => {
    dispatch(getSavedAssessments());
    dispatch(getTakenAssessments());
  }, []);

  const onVisibilityChange = isVisible => {
    if (_isEmpty(pageData)) return null;
    const {
      fields,
      fields: { blog, name, title },
      sys,
    } = pageData[0];
    let type = getFormattedType(sys.contentType.sys.id);
    if (blog) type = 'blog';
    else if (_get(fields, 'type') === 'News') type = 'news';
    if (isVisible && scrolling) {
      Mixpanel.track('Resource Page - ScrollTo', {
        path: window.location.pathname,
        section: 'other-resources-panel',
        type,
        name: name || title,
        entryId: sys.id,
      });
    }
    return null;
  };

  return useMemo(() => {
    if (_isEmpty(data)) return null;

    return (
      <Box component="div" className={classes.root}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <VisibilitySensor
              onChange={onVisibilityChange}
              scrollCheck
              scrollDelay={500}
            >
              <Typography variant="h5" color="textPrimary">
                {header}
              </Typography>
            </VisibilitySensor>
          </Grid>
          <Grid item xs={12} className={classes.carouselContainer}>
            <Carousel
              bigArrow={isMobile || isTablet}
              slidesToSlide={isMobile ? 1 : isTablet ? 2 : col}
              swipeable
              responsive={{
                desktop: {
                  breakpoint: {
                    max: 3000,
                    min: 1024,
                  },
                  items: col,
                  partialVisibilityGutter: 40,
                },
                mobile: {
                  breakpoint: {
                    max: 480,
                    min: 0,
                  },
                  items: 1,
                  partialVisibilityGutter: 30,
                },
                tablet: {
                  breakpoint: {
                    max: 1024,
                    min: 480,
                  },
                  items: 2,
                  partialVisibilityGutter: 30,
                },
              }}
            >
              {filterSelfReferencingResources(data).map(item => (
                <ResourceItemSelector
                  key={item.objectID}
                  item={{
                    ...item,
                    id: item.objectID,
                    type: item.expandedType,
                  }}
                  recommendation
                />
              ))}
            </Carousel>
          </Grid>
        </Grid>
      </Box>
    );
  }, [data]);
};

ItemPageResourcesRow.propTypes = {
  col: PropTypes.number,
  data: PropTypes.array,
  header: PropTypes.string,
  isMobile: PropTypes.bool,
  isTablet: PropTypes.bool,
  isResourcePage: PropTypes.bool,
  pageData: PropTypes.object,
};

const mapSizesToProps = ({ width }) => ({
  isMobile: width < 480,
  isTablet: width <= 1024,
});

export default withSizes(mapSizesToProps)(ItemPageResourcesRow);
